import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  Music, Megaphone, Users, Sparkles, ExternalLink, Phone,
  MapPin, Heart, Star, ArrowRight, Headphones, Disc, Search, Video,
  Film, Palette, Globe, MonitorSmartphone
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6 }
};

const stagger = {
  animate: { transition: { staggerChildren: 0.15 } }
};

const SERVICES = [
  {
    icon: Music,
    title: "Music Events",
    description: "Curating unforgettable live music experiences that bring Twin Cities artists and audiences together.",
    color: "from-purple-500 to-violet-600"
  },
  {
    icon: Headphones,
    title: "Audio Engineering",
    description: "Professional mixing, mastering, and sound design to give your tracks a polished, industry-ready sound.",
    color: "from-pink-500 to-rose-600"
  },
  {
    icon: Disc,
    title: "Music Production",
    description: "With our network of verified artists and decades of professional experience, we focus on taking your compositions to the next level for recording or live performance.",
    color: "from-indigo-500 to-purple-600"
  },
  {
    icon: Search,
    title: "A&R",
    description: "Scouting and developing emerging Twin Cities talent, connecting artists with the right opportunities and industry relationships.",
    color: "from-sky-500 to-blue-600"
  },
  {
    icon: Video,
    title: "Live Show Recording",
    description: "Professional audio and video capture of live performances — multi-track recording, live mixing, and high-quality video production.",
    color: "from-red-500 to-rose-600"
  },
  {
    icon: Megaphone,
    title: "Promotional Campaigns",
    description: "Full-spectrum promotion including video content creation, ad design, social media management, and website creation & management.",
    color: "from-blue-500 to-cyan-600",
    subServices: ["Video Content", "Ad Creations", "Social Media Management", "Website Creation & Management"]
  },
  {
    icon: Users,
    title: "Community Enrichment",
    description: "Building bridges between artists, venues, and communities to strengthen the Twin Cities creative scene.",
    color: "from-emerald-500 to-teal-600"
  },
  {
    icon: Sparkles,
    title: "Talent Showcases",
    description: "Providing platforms for emerging artists to shine and connect with industry professionals.",
    color: "from-amber-500 to-orange-600"
  }
];

const TESTIMONIALS = [
  { handle: "@freakwhenseee", text: "Congrats! Love your place!! Thanks for being in the neighborhood!", img: "https://merlin-cloudinary.b-cdn.net/dftjvuiin/image/upload/v1774439590/mysterymachine_tc/comments/hjvg1j5lu39svry2qocu.jpg" },
  { handle: "@kerisimonsondance", text: "Love the energy and what you're building for the community!", img: "https://merlin-cloudinary.b-cdn.net/dftjvuiin/image/upload/v1774439590/mysterymachine_tc/comments/hustyk84nga60lfw1mel.jpg" },
  { handle: "@cecilia__ruth", text: "Incredible - right on my block!! Cannot wait to listen in!", img: "https://merlin-cloudinary.b-cdn.net/dftjvuiin/image/upload/v1774439592/mysterymachine_tc/comments/sd8nqjodrata48mhxlmc.jpg" },
  { handle: "@marvelous.leonardo", text: "Amazing, love to see it!", img: "https://merlin-cloudinary.b-cdn.net/dftjvuiin/image/upload/v1774439592/mysterymachine_tc/comments/xvkf63zq9tcrw7poztwt.jpg" },
  { handle: "@_micahangel", text: "Lookin forward to this series!", img: "https://merlin-cloudinary.b-cdn.net/dftjvuiin/image/upload/v1774439588/mysterymachine_tc/comments/eiyrcfiybvht1qwmuk6c.jpg" },
  { handle: "@theweepingcovenant", text: "The vibe is unmatched. Keep pushing the culture!", img: "https://merlin-cloudinary.b-cdn.net/dftjvuiin/image/upload/v1774439589/mysterymachine_tc/comments/cxyrju95gcjyus7zxomr.jpg" },
];

const GALLERY = [
  "https://merlin-cloudinary.b-cdn.net/dftjvuiin/image/upload/v1774439577/mysterymachine_tc/rnd57a0jwk78uaupaga0.jpg",
  "https://merlin-cloudinary.b-cdn.net/dftjvuiin/image/upload/v1774439577/mysterymachine_tc/n0hftrumnrecscbod3n1.jpg",
  "https://merlin-cloudinary.b-cdn.net/dftjvuiin/image/upload/v1774439577/mysterymachine_tc/ysxdmst2t667vh7af19o.jpg",
  "https://merlin-cloudinary.b-cdn.net/dftjvuiin/image/upload/v1774439577/mysterymachine_tc/ztwlw1ie6yetbhvhqqyr.jpg",
  "https://merlin-cloudinary.b-cdn.net/dftjvuiin/image/upload/v1774439577/mysterymachine_tc/luldxojgc2khavjyv2lq.jpg",
];

export default function AboutUs() {
  return (
    <div className="min-h-screen" data-testid="about-us-page">
      {/* Hero Section */}
      <section className="relative overflow-hidden py-16 sm:py-24">
        <div className="absolute inset-0 bg-gradient-to-br from-purple-900/30 via-background to-blue-900/20" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-violet-600/10 via-transparent to-transparent" />

        <motion.div
          className="relative max-w-5xl mx-auto px-4 sm:px-6"
          variants={stagger}
          initial="initial"
          animate="animate"
        >
          <div className="flex flex-col md:flex-row items-center gap-8 md:gap-12">
            {/* TC Henry Photo */}
            <motion.div variants={fadeUp} className="shrink-0">
              <div className="relative">
                <div className="w-48 h-48 sm:w-56 sm:h-56 rounded-2xl overflow-hidden border-2 border-purple-500/30 shadow-2xl shadow-purple-500/20">
                  <img
                    src="https://merlin-cloudinary.b-cdn.net/dftjvuiin/image/upload/v1774439576/mysterymachine_tc/zrn1kiwfusogd5v1wz12.jpg"
                    alt="TC Henry - Mystery Machine Productions"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="absolute -bottom-3 -right-3 bg-gradient-to-r from-purple-600 to-blue-500 rounded-xl px-3 py-1.5 text-xs font-bold text-white shadow-lg">
                  TC Henry
                </div>
              </div>
            </motion.div>

            {/* About Text */}
            <motion.div variants={fadeUp} className="text-center md:text-left">
              <p className="text-purple-400 font-semibold tracking-wide uppercase text-sm mb-2">
                About Mystery Machine
              </p>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight text-foreground mb-4">
                Your Twin Cities<br />
                <span className="bg-gradient-to-r from-purple-400 via-violet-400 to-blue-400 bg-clip-text text-transparent">
                  Experience Generator
                </span>
              </h1>
              <p className="text-muted-foreground text-base sm:text-lg leading-relaxed max-w-xl">
                I'm TC Henry, founder of Mystery Machine Productions — a full-service entertainment production and marketing agency built from the ground up in the Twin Cities.
                From live music events and professional audio engineering to artist development and promotional campaigns, we exist to put local talent on the map.
              </p>
              <p className="text-muted-foreground text-base sm:text-lg leading-relaxed mt-3 max-w-xl">
                My commitment is to uplift and celebrate the creative pulse of our community through engaging promotions,
                exciting events and meaningful partnerships. With a deep network of verified artists who can adapt to any special event or occasion,
                along with strong connections to venues and creatives across the metro, we're not just promoting the scene — we're building it.
                If you're ready to be heard, seen, and supported, let's connect.
              </p>

              <div className="flex flex-wrap gap-3 mt-6 justify-center md:justify-start">
                <a href="https://linktr.ee/mysterymachinetc" target="_blank" rel="noopener noreferrer">
                  <Button className="bg-gradient-to-r from-purple-600 to-blue-500 hover:from-purple-700 hover:to-blue-600 text-white gap-2" data-testid="about-contact-btn">
                    Connect With Us <ArrowRight size={16} />
                  </Button>
                </a>
                <Link to="/register/band">
                  <Button variant="outline" className="gap-2 border-purple-500/30 hover:bg-purple-500/10" data-testid="about-register-btn">
                    Register as Artist <Music size={16} />
                  </Button>
                </Link>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </section>

      {/* Mission Divider */}
      <section className="border-y border-border/50 bg-card/30 backdrop-blur-sm">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10 text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <div className="flex items-center justify-center gap-2 mb-3">
              <Heart size={18} className="text-red-400" />
              <p className="text-sm font-semibold text-purple-400 uppercase tracking-wider">Our Mission</p>
            </div>
            <p className="text-lg sm:text-xl text-foreground font-medium leading-relaxed">
              To spotlight and uplift the Twin Cities' creative community — connecting artists, venues,
              businesses, and audiences through the power of music, culture, and shared experiences.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-14 sm:py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-10"
          >
            <p className="text-purple-400 font-semibold text-sm uppercase tracking-wider mb-1">What We Do</p>
            <h2 className="text-2xl sm:text-3xl font-bold text-foreground">Services</h2>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-4">
            {SERVICES.map((service, i) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
              >
                <Card className="bg-card/50 border-border/50 hover:border-purple-500/30 transition-all duration-300 group h-full" data-testid={`service-card-${i}`}>
                  <CardContent className="p-5 flex gap-4 items-start">
                    <div className={`shrink-0 w-11 h-11 rounded-xl bg-gradient-to-br ${service.color} flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform`}>
                      <service.icon size={20} className="text-white" />
                    </div>
                    <div>
                      <h3 className="font-bold text-foreground mb-1">{service.title}</h3>
                      <p className="text-sm text-muted-foreground leading-relaxed">{service.description}</p>
                      {service.subServices && (
                        <div className="flex flex-wrap gap-1.5 mt-2">
                          {service.subServices.map((sub) => (
                            <span key={sub} className="text-xs px-2 py-0.5 rounded-full bg-blue-500/10 text-blue-400 border border-blue-500/20">
                              {sub}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery */}
      <section className="py-10 sm:py-14 border-y border-border/50 bg-card/20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-8"
          >
            <p className="text-purple-400 font-semibold text-sm uppercase tracking-wider mb-1">In Action</p>
            <h2 className="text-2xl sm:text-3xl font-bold text-foreground">Gallery</h2>
          </motion.div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
            {GALLERY.map((src, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                className="aspect-square rounded-xl overflow-hidden border border-border/50 hover:border-purple-500/40 transition-all duration-300 group"
              >
                <img
                  src={src}
                  alt={`Gallery ${i + 1}`}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-14 sm:py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-10"
          >
            <div className="flex items-center justify-center gap-2 mb-1">
              <Star size={16} className="text-amber-400" />
              <p className="text-purple-400 font-semibold text-sm uppercase tracking-wider">Community Love</p>
              <Star size={16} className="text-amber-400" />
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold text-foreground">What People Are Saying</h2>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {TESTIMONIALS.map((t, i) => (
              <motion.div
                key={t.handle}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
              >
                <Card className="bg-card/50 border-border/50 hover:border-purple-500/20 transition-all h-full" data-testid={`testimonial-${i}`}>
                  <CardContent className="p-4 flex gap-3 items-start">
                    <img
                      src={t.img}
                      alt={t.handle}
                      className="w-10 h-10 rounded-full object-cover shrink-0 border border-border/50"
                      loading="lazy"
                    />
                    <div className="min-w-0">
                      <p className="text-xs font-bold text-purple-400 mb-1">{t.handle}</p>
                      <p className="text-sm text-muted-foreground leading-relaxed">"{t.text}"</p>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="pb-16 sm:pb-24">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Card className="bg-gradient-to-br from-purple-900/40 via-card to-blue-900/30 border-purple-500/20">
              <CardContent className="p-8 sm:p-10">
                <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-3">
                  Ready to Connect?
                </h2>
                <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                  Whether you're an artist looking for your next stage, a venue seeking fresh talent,
                  or a business wanting to reach the local scene — let's build something together.
                </p>
                <div className="flex flex-wrap gap-3 justify-center">
                  <a href="https://linktr.ee/mysterymachinetc" target="_blank" rel="noopener noreferrer">
                    <Button size="lg" className="bg-gradient-to-r from-purple-600 to-blue-500 hover:from-purple-700 hover:to-blue-600 text-white gap-2" data-testid="about-cta-contact">
                      Connect With Us <ArrowRight size={16} />
                    </Button>
                  </a>
                  <Link to="/register/band">
                    <Button size="lg" variant="outline" className="border-purple-500/30 hover:bg-purple-500/10 gap-2" data-testid="about-cta-register">
                      Register as Artist <Music size={16} />
                    </Button>
                  </Link>
                </div>
                <div className="flex items-center justify-center gap-4 mt-6 text-sm text-muted-foreground">
                  <span className="flex items-center gap-1.5"><MapPin size={14} /> Twin Cities, MN</span>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
